import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Grid,
	TableContainer,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Skeleton,
	ListSubheader,
	Link,
} from '@mui/material';
import { Formik, Field } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../../shared/context';
import {
	DealRequestAllocation,
	TrancheEvaluation,
	StructureTranches,
	SearchOtherDealsResultItem,
} from '../../shared/models';
import { MatchingUtils, FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { GetStructureTrancheOrganizedList, GetStructureTrancheValue } from '../../shared/utils/enum';
import { PlatformRootUrl } from '../../shared/utils/platform';
import { CerebroCheckbox } from '../../shared/components/CerebroCheckbox';
import { CerebroDrawer } from '../../shared/components/CerebroDrawer';
import { CerebroDropdown, CerebroDropdownMenuItem } from '../../shared/components/CerebroDropdown';
import { CerebroForm } from '../../shared/components/CerebroForm';
import { CerebroIcon } from '../../shared/components/CerebroIcon';
import { CerebroInput } from '../../shared/components/CerebroInput';
import { CerebroLabel } from '../../shared/components/CerebroLabel';
import { CerebroPrimaryButton } from '../../shared/components/CerebroPrimaryButton';
import { CerebroSecondaryButton } from '../../shared/components/CerebroSecondaryButton';
import { CerebroHeading } from '../../shared/components/CerebroHeading';
import { QueryKeys } from '../../shared/hooks/Queries';
import { useParams } from 'react-router-dom';
import { getApiResponseData } from '../../shared/utils/axios';
import { LenderStoreDot } from '../../shared/components';

interface MatchingFromDealDrawerProps {
	open: boolean;
	close: () => void;
	dealGuid?: string;
	allocation?: DealRequestAllocation;
	tranches?: TrancheEvaluation[];
}

export const MatchingFromDealDrawer: React.FC<MatchingFromDealDrawerProps> = ({
	open,
	close,
	dealGuid,
	allocation,
	tranches,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const queryClient = useQueryClient();
	const { guid } = useParams<'guid'>();

	// search fields
	const [companyName, setCompanyName] = React.useState<string>('');
	const [dealName, setDealName] = React.useState<string>('');
	const [teaserFilter, setTeaserFilter] = React.useState<number>(1);

	// teaser form fields
	const [structureType, setStructureType] = React.useState<string>('SeniorCashFlow');
	const [addToDataRoom, setAddToDataRoom] = React.useState<boolean>(false);

	// display fields
	const [isSearching, setIsSearching] = React.useState<boolean>(false);
	const [results, setResults] = React.useState<SearchOtherDealsResultItem[]>([] as SearchOtherDealsResultItem[]);

	// selected tranches
	const selectedTranches = tranches?.filter(
		tranche =>
			MatchingUtils.isTranchePreferenceSelectedByCerebro(
				allocation,
				tranche.structureTranche,
				tranche.tier1BankType
			) && tranche.showEvaluation
	);
	const trancheList = GetStructureTrancheOrganizedList(selectedTranches);

	const resetSearchTab = () => {
		setCompanyName('');
		setDealName('');
		setTeaserFilter(1);
		setStructureType('SeniorCashFlow');
		setResults([] as SearchOtherDealsResultItem[]);
		setIsSearching(false);
		setAddToDataRoom(false);
	};

	const searchLenders = () => {
		setIsSearching(true);
		setResults([] as SearchOtherDealsResultItem[]);
		searchMutation.mutate({
			values: {
				companyName: encodeURIComponent(companyName),
				dealName: encodeURIComponent(dealName),
				teaserFilter,
			},
		} as LendersSearchMutationVariables);
	};

	const searchMutation = useMutation(
		async ({ values }: LendersSearchMutationVariables) => {
			const { data, error } = await getApiResponseData<SearchOtherDealsResultItem[]>(
				api.post(CerebroApiEndpoints.Matching.OtherDeals(dealGuid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: (data: SearchOtherDealsResultItem[] | null) => {
				setIsSearching(false);
				setResults(data ?? []);
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
				setIsSearching(false);
			},
		}
	);

	const lendersMutation = useMutation(
		async ({ values }: LendersSearchValuesMutationVariables) => {
			const { data, error } = await getApiResponseData<void>(
				addToDataRoom
					? api.post(
							CerebroApiEndpoints.Matching.AddToDataRoom(
								dealGuid,
								GetStructureTrancheValue(structureType as StructureTranches)
							),
							values.dataRoomTeaserGuids
					  )
					: api.post(
							CerebroApiEndpoints.Matching.AddToTeasers(
								dealGuid,
								GetStructureTrancheValue(structureType as StructureTranches)
							),
							values.dataRoomTeaserGuids
					  )
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: (_data, { helpers }) => {
				queryClient.invalidateQueries(QueryKeys.Teasers.Root(guid));
				queryClient.invalidateQueries(QueryKeys.DataRoom.Confirmation(guid));
				const successMessage = addToDataRoom
					? 'Successfully added selected lenders to the data room participant list'
					: 'Successfully added selected lenders to the teaser list';
				snackbar.enqueueSnackbar(successMessage, {
					variant: 'success',
				});
				helpers.resetForm({
					values: {
						dataRoomTeaserGuids: [] as string[],
					},
				});
				resetSearchTab();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={open} onClose={close} PaperProps={{ sx: { width: '900px' } }}>
			<CerebroHeading size="h1" component="h1">
				Search for Lender
			</CerebroHeading>

			<Grid container spacing={2} alignItems="stretch">
				<Grid item xs={12} lg={3}>
					<CerebroInput
						value={companyName}
						disabled={isSearching}
						placeholder="Company Name"
						onChange={data => setCompanyName(data.target.value)}
					/>
				</Grid>
				<Grid item xs={12} lg={3}>
					<CerebroInput
						value={dealName}
						disabled={isSearching}
						placeholder="Deal Name"
						onChange={data => setDealName(data.target.value)}
					/>
				</Grid>
				<Grid item xs={12} lg={3}>
					<CerebroDropdown
						value={teaserFilter}
						disabled={isSearching}
						onChange={data => setTeaserFilter(Number.parseInt(data.target.value as string))}
					>
						<CerebroDropdownMenuItem value="0">All</CerebroDropdownMenuItem>
						<CerebroDropdownMenuItem value="1">Accepted Only</CerebroDropdownMenuItem>
					</CerebroDropdown>
				</Grid>
				<Grid item xs={12} lg={3}>
					<CerebroPrimaryButton
						sx={{ marginLeft: 1, marginTop: 1.5 }}
						disabled={isSearching || (!companyName && !dealName)}
						onClick={() => searchLenders()}
					>
						Search
					</CerebroPrimaryButton>
				</Grid>
			</Grid>

			<Formik
				onSubmit={FormikUtils.MutateOnSubmit(lendersMutation.mutate)}
				initialValues={{ dataRoomTeaserGuids: [] as string[] } as LendersSearchValues}
			>
				{({ isSubmitting, handleSubmit, values, setFieldValue }) => (
					<CerebroForm flatIndicator onSubmit={handleSubmit}>
						<TableContainer>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>Select</TableCell>
										<TableCell>Company Name</TableCell>
										<TableCell>Lender Name</TableCell>
										<TableCell>Bank Name</TableCell>
										<TableCell>Status</TableCell>
										<TableCell>Criteria</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{isSearching && (
										<>
											<TableRow>
												<TableCell colSpan={6}>
													<Skeleton />
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell colSpan={6}>
													<Skeleton />
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell colSpan={6}>
													<Skeleton />
												</TableCell>
											</TableRow>
										</>
									)}

									{!isSearching &&
										results.length > 0 &&
										results.map(lender => {
											return (
												<TableRow key={lender.dataRoomTeaserId}>
													<TableCell>
														{lender.isPicked ? (
															<CerebroIcon
																padLeft
																sx={theme => ({
																	color: theme.palette.success.main,
																})}
																title="Already Added to Deal"
															>
																<FontAwesomeIcon icon={faCheck} size="sm" />
															</CerebroIcon>
														) : lender.isExcluded ? (
															<CerebroIcon
																padLeft
																sx={theme => ({
																	color: theme.palette.error.main,
																})}
																title="Excluded Bank of Deal"
															>
																<FontAwesomeIcon icon={faTimes} size="sm" />
															</CerebroIcon>
														) : (
															<Field
																key={lender.dataRoomTeaserGuid}
																as={CerebroCheckbox}
																name={lender.dataRoomTeaserGuid}
																disabled={isSubmitting}
																checked={
																	values.dataRoomTeaserGuids.find(
																		w => w == lender.dataRoomTeaserGuid
																	) != null
																}
																onChange={(e: any) => {
																	if (e.target.checked) {
																		values.dataRoomTeaserGuids.push(e.target.name);
																		setFieldValue(
																			'dataRoomTeaserGuids',
																			values.dataRoomTeaserGuids
																		);
																	} else {
																		const id = values.dataRoomTeaserGuids.indexOf(
																			e.target.name
																		);
																		if (id > -1) {
																			values.dataRoomTeaserGuids.splice(id, 1);
																			setFieldValue(
																				'dataRoomTeaserGuids',
																				values.dataRoomTeaserGuids
																			);
																		}
																	}
																}}
															/>
														)}
													</TableCell>
													<TableCell>
														{lender.companyName}
														{lender.dealName && <div>{lender.dealName}</div>}
													</TableCell>
													<TableCell>
														<LenderStoreDot
															score={lender.score}
															isFlagged={lender.lenderScoreFlag}
															sx={{ marginRight: 0.5 }}
														/>
														{lender.lastName}, {lender.firstName}
													</TableCell>
													<TableCell>{lender.bankName}</TableCell>
													<TableCell>{lender.decisionStatus}</TableCell>
													<TableCell>
														<Link
															target="_blank"
															href={`${PlatformRootUrl}/BankDealProfiles/UnderwritingCriteria/${lender.bankContactGuid}`}
														>
															View
														</Link>
													</TableCell>
												</TableRow>
											);
										})}
									{results.length == 0 && (
										<TableRow>
											<TableCell colSpan={5} sx={{ textAlign: 'center' }}>
												No results, please select search criteria.
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>

						<Grid container sx={{ marginTop: 3 }}>
							<Grid item xs={12} lg={6}>
								<CerebroLabel>Which tranche will the lenders be linked to?</CerebroLabel>

								<CerebroDropdown
									value={structureType}
									disabled={isSearching || !Object.values(values).some(checked => checked)}
									onChange={data => setStructureType(data.target.value as string)}
								>
									{trancheList.map(tranche => {
										if (tranche.isOptionGroup) {
											return <ListSubheader key={tranche.text}>{tranche.text}</ListSubheader>;
										} else {
											return (
												<CerebroDropdownMenuItem key={tranche.value} value={tranche.value}>
													{tranche.text}
												</CerebroDropdownMenuItem>
											);
										}
									})}
								</CerebroDropdown>
							</Grid>
						</Grid>

						<CerebroPrimaryButton
							type="submit"
							loading={isSubmitting}
							disabled={!values.dataRoomTeaserGuids || values.dataRoomTeaserGuids.length == 0}
							sx={{ marginTop: 6, marginRight: 1 }}
							onClick={() => setAddToDataRoom(false)}
						>
							Add to Teaser List
						</CerebroPrimaryButton>

						<CerebroSecondaryButton
							type="submit"
							loading={isSubmitting}
							disabled={!values.dataRoomTeaserGuids || values.dataRoomTeaserGuids.length == 0}
							sx={{ marginTop: 6 }}
							onClick={() => setAddToDataRoom(true)}
						>
							Add to Data Room
						</CerebroSecondaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type LendersSearchMutationVariables = FormikUtils.MutateOnSubmitVariables<LendersSearch>;
interface LendersSearch {
	companyName: string;
	dealName: string;
	teaserFilter: number;
}

type LendersSearchValuesMutationVariables = FormikUtils.MutateOnSubmitVariables<LendersSearchValues>;
interface LendersSearchValues {
	dataRoomTeaserGuids: string[];
}
